import { FC, useEffect, useRef, useState } from "react";
import styles from "./Properties.module.scss";
import { useCatalogFilter } from "@/lib/hooks/useCatalogFilter";
import { P3, P2 } from "@/lib/ui/components/Typography";
import { useTranslation } from "next-i18next";
import { SortDefaultIcon, SortDirectionIcon } from "@/lib/ui/icons";
import cl from "classnames";
import useOnClickOutside from "@/lib/hooks/useOnClickOutside";

const sortOptions = [
  {
    sort: "default",
    sortDir: "",
    label: "byDefault",
    chosenLabel: "byDefault",
    additional: false,
  },
  {
    sort: "price",
    sortDir: "desc",
    label: "cheapestFirst",
    chosenLabel: "byPrice",
    additional: false,
  },
  {
    sort: "price",
    sortDir: "asc",
    label: "expensiveFirst",
    chosenLabel: "byPrice",
    additional: false,
  },
  {
    sort: "distance",
    sortDir: "asc",
    label: "closerToBeach",
    chosenLabel: "byDistance",
    additional: false,
  },
  {
    sort: "area",
    sortDir: "asc",
    label: "biggerArea",
    chosenLabel: "byArea",
    additional: false,
  },
  {
    sort: "area",
    sortDir: "desc",
    label: "smallerArea",
    chosenLabel: "byArea",
    additional: false,
  },
  {
    sort: "date",
    sortDir: "desc",
    label: "readySoon",
    chosenLabel: "byDate",
    additional: true,
  },
  {
    sort: "date",
    sortDir: "asc",
    label: "readyNotThisYear",
    chosenLabel: "byDate",
    additional: true,
  },
] as const;

interface Params {
  sort?: "area" | "price" | "distance" | "date" | "default" | undefined;
  sortDir?: "asc" | "desc" | "";
}

export const PropertiesSort: FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const optionsRef = useRef<HTMLUListElement>(null);
  const { query, setParam, router } = useCatalogFilter();
  const { t } = useTranslation("index");
  const sortDirection = query.sortDir === "desc" ? "desc" : "asc";
  const openMenu = () => setIsMenuOpen(true);
  const closeMenu = () => setIsMenuOpen(false);
  const includeAdditional =
    query.buildingStages === "stillBuilding" ||
    query.buildingStages?.includes("stillBuilding");

  const [chosenLabel, setChosenLabel] =
    useState<(typeof sortOptions)[number]["chosenLabel"]>("byDefault");
  useEffect(() => {
    if (router.isReady && query.sort) {
      setChosenLabel(
        sortOptions.find((option) => option.sort === query.sort)!.chosenLabel,
      );
    }
    if (router.isReady && !query.sort) {
      setChosenLabel("byDefault");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, query.sort]);

  const onOptionClick = (option: (typeof sortOptions)[number]) => {
    const params: Params = {
      sort: option.sort,
      sortDir: option.sortDir,
    };
    if (option.sort === "default") {
      delete query.sort;
      delete query.sortDir;
      setParam(undefined, { ...query });
    } else {
      setParam(undefined, { ...params });
    }
    setChosenLabel(option.chosenLabel);
    closeMenu();
  };

  useOnClickOutside(optionsRef, closeMenu);

  return (
    <div className={styles.sort}>
      <P3 className={styles.sort__text}>{t("catalog.sortBy")}:</P3>
      {!query.sort ? (
        <SortDefaultIcon />
      ) : (
        <SortDirectionIcon upsideDown={sortDirection === "desc"} />
      )}
      <button className={styles.sort__button} onClick={openMenu}>
        <P2 key={chosenLabel}>{t(`catalog.${chosenLabel}`)}</P2>
      </button>
      <ul
        ref={optionsRef}
        className={cl(styles.sort__options, isMenuOpen && styles["sort__options--open"])}
      >
        {sortOptions
          .filter((item) => (includeAdditional ? item : !item.additional))
          .map((option) => {
            return (
              <li
                key={option.label}
                className={styles.sort__option}
                onClick={() => onOptionClick(option)}
              >
                {t(`catalog.${option.label}`)}
              </li>
            );
          })}
      </ul>
    </div>
  );
};
